import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { QCRecordsListResult } from "@/modules/qc/domain/types/record";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useDeleteQCRecord, useQCRecordsQuery } from "../api/queries";
import { QCRecordTour, useQCRecordTour } from "../tour/QCRecordTour";
import { QCRecordFilter } from "../ui/QCRecordFilter";
import { QCRecordTable } from "../ui/QCRecordTable";
import { QCRecordToolbar } from "../ui/QCRecordToolbar";

export function QCRecordListPage() {
  const nav = useNavigate({ from: "/app/qc/records" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/qc/records" });
  const tour = useQCRecordTour();
  const hasActiveFilters = (search.filters?.length ?? 0) > 0;

  const list = useQCRecordsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["qc_record:create"]);
  const deleteMutation = useDeleteQCRecord();

  const handleDelete = (record: QCRecordsListResult["data"][number]) => {
    deleteMutation.mutate(record.id);
  };

  const handleEdit = (record: QCRecordsListResult["data"][number]) => {
    nav({
      to: "/app/qc/$id/record/$recordId",
      params: { id: record.qcStandardId, recordId: record.id },
    });
  };

  const handleView = (id: string) => {
    // Navigate to QC record detail page
    nav({
      to: "/app/qc/records/$recordId",
      params: { recordId: id },
    });
  };

  return (
    <>
      <Header />

      <Main>
        <QCRecordTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <QCRecordToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/qc" })}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <QCRecordFilter />

          {list.isError && (
            <div className="mx-2 mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
              <span className="text-destructive">
                ໂຫຼດລາຍການບໍ່ສຳເລັດ — ກະລຸນາລອງອີກຄັ້ງ
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => list.refetch()}
              >
                ລອງໃໝ່
              </Button>
            </div>
          )}

          {!list.isLoading &&
            !list.isError &&
            list.data?.meta?.total === 0 &&
            hasActiveFilters && (
              <p className="px-2 pb-2 text-muted-foreground text-sm">
                ບໍ່ພົບລາຍການທີ່ກົງກັບຕົວກອງ — ລອງກົດ &quot;ລ້າງ&quot; ຫຼື ປ່ຽນປະເພດງານ /
                ສະຖານະ
              </p>
            )}

          <QCRecordTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={handleEdit}
            onView={handleView}
            onDelete={handleDelete}
          />
        </div>
      </Main>
    </>
  );
}
